/**
 * @fileoverview Componente responsável por editar prompts e memórias usados nos nós do fluxo.
 */

import React from "react";
import { Tooltip } from "@material-ui/core";
import {
  Bot, FileUp, Minimize2, Brain, SquareKanban, RefreshCw, ArrowRightLeft,
} from "lucide-react";

/**
 * Builds the sidebar items from connected tool nodes.
 */
var buildSidebarItems = function (agentId, edges, nodes) {
  var connectedToolIds = edges
    .filter(function (e) { return e.source === agentId && e.sourceHandle === "tools"; })
    .map(function (e) { return e.target; });

  var items = [];

  nodes.forEach(function (n) {
    if (connectedToolIds.indexOf(n.id) === -1) return;

    if (n.data.nodeType === "tool_files") {
      var nodeFiles = n.data.files || [];
      nodeFiles.forEach(function (f) {
        items.push({ id: f.id, name: f.name, tag: "{{" + f.name + "}}", icon: FileUp, color: "#f59e0b", category: "ARQUIVOS" });
      });
    }

    if (n.data.nodeType === "tool_memory") {
      var memories = n.data.memories || [];
      memories.forEach(function (m) {
        items.push({ id: m.id, name: m.name, tag: "{{memoria_" + m.name.replace(/\s+/g, "_") + "}}", icon: Brain, color: "#ec4899", category: "MEMORIAS" });
      });
    }

    if (n.data.nodeType === "tool_crm_create") {
      items.push({ id: "crm_create_" + n.id, name: "Criar Card CRM", tag: "{{crm_criar_card}}", icon: SquareKanban, color: "#10b981", category: "CRM" });
      var fields = n.data.fields || [];
      fields.forEach(function (f) {
        items.push({ id: "field_" + f.id, name: f.name, tag: "{{crm_campo_" + f.name.replace(/\s+/g, "_") + "}}", icon: SquareKanban, color: "#10b981", category: "CRM" });
      });
    }

    if (n.data.nodeType === "tool_crm_update") {
      items.push({ id: "crm_update_" + n.id, name: "Atualizar Card", tag: "{{crm_atualizar_card}}", icon: RefreshCw, color: "#06b6d4", category: "CRM" });
    }

    if (n.data.nodeType === "tool_crm_status") {
      items.push({ id: "crm_status_" + n.id, name: "Status Card", tag: "{{crm_mover_card}}", icon: ArrowRightLeft, color: "#f97316", category: "CRM" });
    }
  });

  return items;
};

/**
 * Groups items by category.
 */
var groupByCategory = function (items) {
  var groups = {};
  items.forEach(function (item) {
    var cat = item.category || "OUTROS";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  });
  return groups;
};

/**
 * Renders the sidebar with categorized tool items.
 */
var renderSidebar = function (classes, sidebarItems, handleItemClick, handleItemDragStart) {
  var grouped = groupByCategory(sidebarItems);
  var categories = Object.keys(grouped);

  if (categories.length === 0) {
    return (
      <div className={classes.promptModalEmptySidebar}>
        Nenhuma ferramenta conectada. Conecte nodes ao agente para ver aqui.
      </div>
    );
  }

  return categories.map(function (cat) {
    var items = grouped[cat];
    return (
      <div key={cat}>
        <div className={classes.promptModalSidebarTitle} style={{ marginTop: 8 }}>{cat}</div>
        {items.map(function (item) {
          var ItemIcon = item.icon;
          return (
            <div
              key={item.id}
              className={classes.promptModalFileItem}
              draggable
              onDragStart={function (e) { handleItemDragStart(e, item); }}
              onClick={function () { handleItemClick(item); }}
            >
              <div className={classes.promptModalFileIcon} style={{ background: item.color + "18" }}>
                <ItemIcon size={12} color={item.color} strokeWidth={2} />
              </div>
              <div className={classes.promptModalFileName}>{item.name}</div>
            </div>
          );
        })}
      </div>
    );
  });
};

/**
 * Full-screen prompt editor modal for AI Agent nodes.
 */
var PromptModal = function (props) {
  var classes = props.classes;
  var selectedNode = props.selectedNode;
  var edges = props.edges;
  var nodes = props.nodes;
  var updateNodeData = props.updateNodeData;
  var setPromptModalOpen = props.setPromptModalOpen;

  if (!selectedNode || selectedNode.data.nodeType !== "ai_agent") return null;

  var agentId = selectedNode.id;
  var sidebarItems = buildSidebarItems(agentId, edges, nodes);

  var handleItemDragStart = function (e, item) {
    e.dataTransfer.setData("text/plain", item.tag);
    e.dataTransfer.effectAllowed = "copy";
  };

  var handleItemClick = function (item) {
    var ta = document.getElementById("prompt-modal-textarea");
    if (!ta) return;
    var start = ta.selectionStart;
    var end = ta.selectionEnd;
    var current = selectedNode.data.prompt || "";
    var newValue = current.substring(0, start) + item.tag + current.substring(end);
    updateNodeData("prompt", newValue);
    setTimeout(function () {
      ta.focus();
      var pos = start + item.tag.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <div
      className={classes.promptModal}
      onClick={function () { setPromptModalOpen(false); }}
    >
      <div
        className={classes.promptModalContent}
        onClick={function (e) { e.stopPropagation(); }}
      >
        <div className={classes.promptModalHeader}>
          <div className={classes.promptModalTitle}>
            <Bot size={16} color="#8b5cf6" />
            Prompt do Agente IA
          </div>
          <Tooltip title="Fechar">
            <div
              className={classes.promptModalClose}
              onClick={function () { setPromptModalOpen(false); }}
            >
              <Minimize2 size={16} />
            </div>
          </Tooltip>
        </div>
        <div className={classes.promptModalBody}>
          <div className={classes.promptModalSidebar}>
            <div className={classes.promptModalSidebarHint}>
              Arraste ou clique para inserir no prompt
            </div>
            <div className={classes.promptModalSidebarList}>
              {renderSidebar(classes, sidebarItems, handleItemClick, handleItemDragStart)}
            </div>
          </div>

          <div className={classes.promptModalTextareaWrap}>
            <textarea
              id="prompt-modal-textarea"
              className={classes.promptModalTextarea}
              value={selectedNode.data.prompt || ""}
              onChange={function (e) {
                updateNodeData("prompt", e.target.value);
              }}
              onDrop={function (e) {
                e.preventDefault();
                var tag = e.dataTransfer.getData("text/plain");
                if (!tag || tag.indexOf("{{") !== 0) return;
                var ta = e.target;
                var pos = ta.selectionStart;
                var current = selectedNode.data.prompt || "";
                var newValue = current.substring(0, pos) + tag + current.substring(pos);
                updateNodeData("prompt", newValue);
                setTimeout(function () {
                  ta.focus();
                  var newPos = pos + tag.length;
                  ta.setSelectionRange(newPos, newPos);
                }, 0);
              }}
              onDragOver={function (e) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
              placeholder={"Voce e um assistente virtual da empresa X.\nSeja educado, objetivo e responda apenas sobre nossos produtos e servicos.\n\nUse {{nome}} para referenciar ferramentas no prompt."}
              autoFocus
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Full-screen prompt editor modal for Memory content.
 * Same layout as PromptModal but edits memContent instead of agent prompt.
 */
var MemoryPromptModal = function (props) {
  var classes = props.classes;
  var selectedNode = props.selectedNode;
  var edges = props.edges;
  var nodes = props.nodes;
  var memContent = props.memContent;
  var setMemContent = props.setMemContent;
  var setMemPromptModalOpen = props.setMemPromptModalOpen;

  if (!selectedNode) return null;

  // Find the agent node this memory is connected to
  var memNodeId = selectedNode.id;
  var agentEdge = edges.find(function (e) { return e.target === memNodeId; });
  var agentId = agentEdge ? agentEdge.source : null;
  var sidebarItems = agentId ? buildSidebarItems(agentId, edges, nodes) : [];

  var handleItemDragStart = function (e, item) {
    e.dataTransfer.setData("text/plain", item.tag);
    e.dataTransfer.effectAllowed = "copy";
  };

  var handleItemClick = function (item) {
    var ta = document.getElementById("memory-modal-textarea");
    if (!ta) return;
    var start = ta.selectionStart;
    var end = ta.selectionEnd;
    var current = memContent || "";
    var newValue = current.substring(0, start) + item.tag + current.substring(end);
    setMemContent(newValue);
    setTimeout(function () {
      ta.focus();
      var pos = start + item.tag.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <div
      className={classes.promptModal}
      onClick={function () { setMemPromptModalOpen(false); }}
    >
      <div
        className={classes.promptModalContent}
        onClick={function (e) { e.stopPropagation(); }}
      >
        <div className={classes.promptModalHeader}>
          <div className={classes.promptModalTitle}>
            <Brain size={16} color="#ec4899" />
            Conteudo da Memoria
          </div>
          <Tooltip title="Fechar">
            <div
              className={classes.promptModalClose}
              onClick={function () { setMemPromptModalOpen(false); }}
            >
              <Minimize2 size={16} />
            </div>
          </Tooltip>
        </div>
        <div className={classes.promptModalBody}>
          <div className={classes.promptModalSidebar}>
            <div className={classes.promptModalSidebarHint}>
              Arraste ou clique para inserir no prompt
            </div>
            <div className={classes.promptModalSidebarList}>
              {renderSidebar(classes, sidebarItems, handleItemClick, handleItemDragStart)}
            </div>
          </div>

          <div className={classes.promptModalTextareaWrap}>
            <textarea
              id="memory-modal-textarea"
              className={classes.promptModalTextarea}
              value={memContent || ""}
              onChange={function (e) {
                setMemContent(e.target.value);
              }}
              onDrop={function (e) {
                e.preventDefault();
                var tag = e.dataTransfer.getData("text/plain");
                if (!tag || tag.indexOf("{{") !== 0) return;
                var ta = e.target;
                var pos = ta.selectionStart;
                var current = memContent || "";
                var newValue = current.substring(0, pos) + tag + current.substring(pos);
                setMemContent(newValue);
                setTimeout(function () {
                  ta.focus();
                  var newPos = pos + tag.length;
                  ta.setSelectionRange(newPos, newPos);
                }, 0);
              }}
              onDragOver={function (e) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
              placeholder={"Escreva aqui as instrucoes detalhadas para este caso.\n\nUse {{nome}} para referenciar ferramentas."}
              autoFocus
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptModal;
export { MemoryPromptModal };
